import type { MenuProps } from 'antd';
import { Menu } from 'antd';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ADMIN_ROUTE_NAME } from '../../constants/RouteName';
import SidebarLogo from './SidebarLogo';
import './style.scss';
import {
  configurationManagementRoutes,
  termsOfUseAndPolicies,
  staffDepartmentPosition,
  locationManagementRoutes,
} from '../../routes/Admin';
import ButtonWrap from '../../components/ButtonWrap';
import SvgLogOut from '../../components/svg/SvgLogOut';
import { useIntl } from 'react-intl';
import { logout } from '../../store/authSlice';

type MenuItem = Required<MenuProps>['items'][number];

export const getItem = (
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[],
  className?: string,
): MenuItem => {
  return {
    key,
    icon,
    children,
    label,
    className,
  } as MenuItem;
};

interface ISideBarContentProp {
  menuItems?: MenuProps['items'];
}

const SidebarContent = (props: ISideBarContentProp) => {
  const location = useLocation();

  const navigate = useNavigate();
  const [current, setCurrent] = useState<string>(location.pathname);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [isCloseMenu, setIsCloseMenu] = useState<boolean>(false);
  const intl = useIntl();

  const selectedKeys = location.pathname.substring(1);
  const defaultOpenKeys = selectedKeys.split('/')[1];
  const selected = selectedKeys.split('/')[0];

  useEffect(() => {
    if (openKeys.length === 0 && !isCloseMenu) {
      const pathname = location.pathname.split('/')[1];

      if (configurationManagementRoutes.includes(pathname)) {
        setOpenKeys([ADMIN_ROUTE_NAME.CONFIGURATION_MANAGEMENT]);
      } else if (staffDepartmentPosition.includes(pathname)) {
        setOpenKeys([ADMIN_ROUTE_NAME.STAFF_DEPARTMENT_POSITION]);
      } else if (locationManagementRoutes.includes(pathname)) {
        setOpenKeys([ADMIN_ROUTE_NAME.LOCATION_MANAGEMENT]);
      } else if (termsOfUseAndPolicies.includes(pathname)) {
        setOpenKeys([ADMIN_ROUTE_NAME.TERMS_OF_USE_AND_POLICIES]);
      }
    }
  }, [openKeys, location.pathname, isCloseMenu]);

  const onLogout = () => {
    logout();
  };

  const onClick: MenuProps['onClick'] = (e) => {
    setCurrent(e.key);
    navigate(e.key);

    if (
      !configurationManagementRoutes.includes(e.key) &&
      !termsOfUseAndPolicies.includes(e.key) &&
      !staffDepartmentPosition.includes(e.key) &&
      !locationManagementRoutes.includes(e.key)
    ) {
      setOpenKeys([]);
    }
  };

  const onOpenChange: (keys: string[]) => void = (keys) => {
    let navigateTo = '';

    if (keys[keys.length - 1] === ADMIN_ROUTE_NAME.CONFIGURATION_MANAGEMENT) {
      navigateTo = ADMIN_ROUTE_NAME.LOGO;
    } else if (keys[keys.length - 1] === ADMIN_ROUTE_NAME.TERMS_OF_USE_AND_POLICIES) {
      navigateTo = ADMIN_ROUTE_NAME.PRIVACY_POLICY;
    } else if (keys[keys.length - 1] === ADMIN_ROUTE_NAME.STAFF_DEPARTMENT_POSITION) {
      navigateTo = ADMIN_ROUTE_NAME.STAFF_TYPE_MANAGEMENT;
    } else if (keys[keys.length - 1] === ADMIN_ROUTE_NAME.LOCATION_MANAGEMENT) {
      navigateTo = ADMIN_ROUTE_NAME.LOCATION;
    }

    if (navigateTo) {
      navigate(navigateTo);
    }

    if (keys.length > 0) {
      setOpenKeys([keys[keys.length - 1]]);
    } else {
      setIsCloseMenu(true);
      setOpenKeys([]);
    }
  };

  return (
    <div className="sidebar">
      <SidebarLogo />
      <div className="sidebar__line" />
      <Menu
        onClick={onClick}
        onOpenChange={onOpenChange}
        selectedKeys={[selected || selectedKeys || current]}
        openKeys={openKeys}
        defaultOpenKeys={[defaultOpenKeys]}
        mode="inline"
        className="sidebar__menu"
        items={props.menuItems}
      />
      <div className="sidebar__logout">
        <ButtonWrap onClick={onLogout} htmlType="submit" variant="filled" color="danger" width={180} height={44}>
          <SvgLogOut />
          {intl.formatMessage({ id: 'adminMenu.logOut' })}
        </ButtonWrap>
      </div>
    </div>
  );
};

export default SidebarContent;
